/* Microdacyn redesign - motion system (scroll reveal, image load-in, counters, header state, wordmark).

   Reveals are one-shot on purpose. Content that re-hides when you scroll back up is the
   single biggest thing that makes a storefront feel restless, and it fights find-in-page
   and screen readers. What DOES track the scroll both ways is positional, not a state
   toggle - see the `view()` timeline rules in mdc-motion.css.

   IntersectionObserver rather than a scroll handler: no work per frame, and it keeps
   working whichever element Horizon happens to be scrolling (on desktop that is
   .page-wrapper, not the window), because the observer root stays the viewport.

   ONE threshold for everything. An earlier version held the footer card back to a much
   later margin so its wordmark would be on screen when it played; the cost was that if you
   stopped scrolling in the band between the two thresholds, the footer sat there as an
   empty grey rectangle and never resolved. A reveal that depends on you continuing to
   scroll is a broken reveal. The wordmark keeps its own per-letter delay in CSS, which is
   the part that was actually worth staggering.

   Every selector listed in REVEAL must also have a matching transition + hidden rule in
   CSS (mdc-motion.css for the shared set, mdc-pages.css / mdc-doc.css / mdc-blog.css /
   mdc-account.css for the per-template ones) or it will appear without animating. */
(() => {
  window.mdcMotion = true;
  const html = document.documentElement;
  const motion = html.classList.contains('mdc-motion');
  const scrollOpts = { capture: true, passive: true };
  const scrollTop = () =>
    Math.max(
      window.scrollY || 0,
      document.scrollingElement ? document.scrollingElement.scrollTop : 0,
      (document.querySelector('.page-wrapper') || {}).scrollTop || 0
    );

  /* Header shadow once scrolled (runs even with motion off) */
  const header = document.querySelector('.mdc-header');
  if (header) {
    const onHeader = () => header.classList.toggle('is-scrolled', scrollTop() > 40);
    onHeader();
    document.addEventListener('scroll', onHeader, scrollOpts);
  }
  if (!motion) return;

  const mobile = window.matchMedia('(max-width: 989px)').matches;

  const REVEAL =
    '.mdc-heading, .mdc-reviews h2.mdc-h2, .mdc-banner, .mdc-feature, .mdc-products .mdc-card, .mdc-center-link,' +
    ' .mdc-finder__panel, .mdc-finder__result, .mdc-review, .mdc-reviews__nav, .mdc-faq__intro, .mdc-faq__item,' +
    ' .mdc-bundle__media, .mdc-bundle__info, .mdc-footer__card, .mdc-gallery, .mdc-proof, .mdc-product__info,' +
    ' .mdc-shop__sidebar, .mdc-shop__grid .mdc-card, .mdc-usage-list__intro, .mdc-about-hero__inner,' +
    ' .mdc-page-banner__inner, .mdc-topic, .mdc-split__body, .mdc-split__media, .mdc-checklist__panel,' +
    ' .mdc-contact__intro, .mdc-contact__panel,' +
    ' .mdc-dochead__card, .mdc-doc__nav, .mdc-rte, .mdc-help__card, .mdc-404__card,' +
    ' .mdc-search__head, .mdc-search__group, .mdc-search__item, .mdc-search__products .mdc-card, .mdc-search__empty,' +
    ' .mdc-post, .mdc-article__head, .mdc-article__media, .mdc-article__body,' +
    ' .mdc-acct__intro, .mdc-acct__route, .mdc-acct__note';
  const STAGGER = [
    '.mdc-feature',
    '.mdc-products .mdc-card',
    '.mdc-review',
    '.mdc-faq__item',
    '.mdc-shop__grid .mdc-card',
    '.mdc-contact__detail',
    '.mdc-search__item',
    '.mdc-search__products .mdc-card',
    '.mdc-post',
    '.mdc-acct__route',
  ];

  /* Footer wordmark: split into letters so they can rise in one by one */
  document.querySelectorAll('.mdc-footer__wordmark').forEach((el) => {
    const text = el.textContent.trim();
    el.setAttribute('aria-label', text);
    el.innerHTML = [...text]
      .map((ch, i) => `<span class="mdc-char" style="--i:${i}">${ch === ' ' ? '&nbsp;' : ch}</span>`)
      .join('');
  });

  const items = [...document.querySelectorAll(REVEAL)];

  /* Stagger is a small luxury on a mouse and a delay you feel on a phone, so it is
     roughly halved on mobile and capped so a long grid never trails far behind. */
  const step = mobile ? 0.05 : 0.09;
  items.forEach((el) => {
    if (!STAGGER.some((s) => el.matches(s)) || !el.parentElement) return;
    const siblings = [...el.parentElement.children].filter((c) => c.matches(REVEAL));
    const index = Math.min(siblings.indexOf(el), 5);
    if (index > 0) el.style.setProperty('--mdc-delay', `${(index * step).toFixed(2)}s`);
  });

  const reveal = (el) => {
    el.classList.add('is-in');
    /* drop the delay once it has played so a later re-layout does not re-stagger */
    setTimeout(() => el.style.removeProperty('--mdc-delay'), 1600);
  };

  if (!('IntersectionObserver' in window)) {
    items.forEach(reveal);
  } else {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: `0px 0px ${mobile ? '-3%' : '-8%'} 0px`, threshold: 0 }
    );
    items.forEach((el) => observer.observe(el));

    /* safety net: never leave content hidden if an observer never fires */
    setTimeout(() => {
      items.forEach((el) => {
        if (el.classList.contains('is-in')) return;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) reveal(el);
      });
    }, 1800);
  }

  /* Images fade in as they decode, so nothing snaps into place mid-scroll.
     The homepage hero image is skipped: it has its own entrance animation. */
  document.querySelectorAll('.mdc img').forEach((img) => {
    if (img.closest('.mdc-hero__media')) return;
    if (img.complete && img.naturalWidth) return;
    img.classList.add('mdc-img');
    const done = () => img.classList.add('is-loaded');
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  });

  /* Count-up for hero stats */
  document.querySelectorAll('.mdc-stat strong').forEach((el) => {
    const value = el.textContent.trim();
    const target = parseInt(value, 10);
    if (!Number.isFinite(target) || String(target) !== value) return;
    const start = performance.now() + 450;
    const dur = 1400;
    el.textContent = '0';
    const tick = (now) => {
      const t = Math.min(Math.max((now - start) / dur, 0), 1);
      el.textContent = String(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    setTimeout(() => {
      el.textContent = value;
    }, dur + 1200);
  });
})();
